import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, UserCog, Puzzle, Building2, ShieldAlert, Target, KeyRound } from 'lucide-react';
import { useUserStore } from '../store/userStore';
import { MySettings } from '../components/settings/MySettings';
import { PeopleRoles } from '../components/settings/PeopleRoles';
import { Integrations } from '../components/settings/Integrations';
import { CompanySettings } from '../components/settings/CompanySettings';
import { RiskConfiguration } from '../components/settings/RiskConfiguration';
import { GoalSettings } from '../components/settings/GoalSettings';
import { GeminiKeySettings } from '../components/settings/GeminiKeySettings';

interface Tab {
  id: string;
  label: string;
  icon: React.ElementType;
  adminOnly: boolean;
}

const TABS: Tab[] = [
  { id: 'my', label: 'My Profile', icon: User, adminOnly: false },
  { id: 'people', label: 'People & Roles', icon: UserCog, adminOnly: true },
  { id: 'integrations', label: 'Integrations', icon: Puzzle, adminOnly: true },
  { id: 'company', label: 'Company', icon: Building2, adminOnly: true },
  { id: 'risk', label: 'Risk', icon: ShieldAlert, adminOnly: true },
  { id: 'goals', label: 'Goals', icon: Target, adminOnly: true },
  { id: 'ai-key', label: 'AI Key', icon: KeyRound, adminOnly: true },
];

export function Settings() {
  const { sessionConfig } = useUserStore();
  const [searchParams, setSearchParams] = useSearchParams();
  // The active tab is derived directly from the URL rather than mirrored
  // into separate local state. Previously this component read `?tab=` once
  // at mount and then stripped it, so any later return to Settings —
  // browser back/forward, a bookmark, a Guided Tour link — always reset to
  // "My Profile" instead of the tab the user was actually on (reported as
  // the user's persona/dashboard "changing" when it was really just a lost
  // tab selection). Keeping the URL as the single source of truth means
  // clicking a tab, navigating away, and coming back all agree.
  const requestedTab = searchParams.get('tab') ?? 'my';
  const [connectedProvider, setConnectedProvider] = useState<string | null>(
    () => searchParams.get('connected')
  );
  // Set when the reconnect completed the OAuth handshake but the new
  // credential failed the backend's eager health verification (see
  // backend/app/routers/integrations.py) — distinct from an ordinary
  // successful reconnect so the UI doesn't silently celebrate a broken one.
  const [connectVerifyFailed, setConnectVerifyFailed] = useState<boolean>(
    () => searchParams.get('reconnectVerified') === '0'
  );

  const setActiveTab = (tab: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    next.delete('connected');
    next.delete('reconnectVerified');
    setSearchParams(next, { replace: true });
  };

  // Consume the one-time `?connected=`/`?reconnectVerified=` OAuth-callback
  // flags without touching `tab`.
  useEffect(() => {
    if (!searchParams.get('connected') && !searchParams.get('reconnectVerified')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('connected');
    next.delete('reconnectVerified');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const isAdmin = sessionConfig
    ? sessionConfig.entityRole === 'admin' || sessionConfig.role === 'admin'
    : false;

  const visibleTabs = TABS.filter((t) => !t.adminOnly || isAdmin);

  // A non-admin hitting an adminOnly tab via URL (or an admin session that
  // hasn't loaded yet) used to render a permanently blank pane — the
  // content below gates on `isAdmin` with no fallback, but the tab nav
  // above only ever highlighted a visible tab, so nothing indicated why
  // the pane was empty. Fall back to 'my', which every caller can see.
  const activeTab = visibleTabs.some((t) => t.id === requestedTab) ? requestedTab : 'my';

  return (
    <div className="px-6 py-6 sm:px-10 lg:px-16">
      <div className="mb-8">
        <h1 className="text-headline text-gray-900 dark:text-white font-display">Settings</h1>
        <p className="text-body text-gray-500 dark:text-gray-400 mt-2">
          Manage your account and workspace preferences
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-6">
        {/* Tab nav */}
        <nav className="sm:w-48 flex-shrink-0">
          <ul className="space-y-0.5">
            {visibleTabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <li key={tab.id}>
                  <motion.button
                    whileHover={{ x: 2 }}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-body font-medium focus-visible:ring-2 focus-visible:ring-primary/50 transition-all ${
                      isActive
                        ? 'bg-primary/10 dark:bg-primary/20 text-primary'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/[0.06]'
                    }`}
                  >
                    <tab.icon className="h-4 w-4 flex-shrink-0" />
                    {tab.label}
                  </motion.button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Tab content — left-anchored with a capped measure, the briefing's
            container model: the shell runs full width, reading/form content
            keeps a readable measure (was max-w-6xl on the whole page). */}
        <div className="flex-1 min-w-0">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.15 }}
          >
            {activeTab === 'my' && <MySettings />}
            {activeTab === 'people' && isAdmin && <PeopleRoles />}
            {activeTab === 'integrations' && isAdmin && (
              <div data-demo-id="integrations-section">
                <Integrations
                  newlyConnected={connectedProvider}
                  newlyConnectedVerifyFailed={connectVerifyFailed}
                  onSyncPromptDismissed={() => {
                    setConnectedProvider(null);
                    setConnectVerifyFailed(false);
                  }}
                />
              </div>
            )}
            {activeTab === 'company' && isAdmin && <CompanySettings />}
            {activeTab === 'risk' && isAdmin && (
              <div data-demo-id="risk-section">
                <RiskConfiguration />
              </div>
            )}
            {activeTab === 'goals' && isAdmin && <GoalSettings />}
            {activeTab === 'ai-key' && isAdmin && <GeminiKeySettings />}
          </motion.div>
        </div>
      </div>
    </div>
  );
}
